import React, { useEffect, useState } from "react";

export default function Hero({
  title = "Your Perfect Stay in Dubai",
  subtitle = "From luxury resorts to boutique hotels, find the perfect place to stay in the city of dreams.",
  backgroundImage,
  backgroundImages = [],
  variant = "inner",
  overlayOpacity = "bg-black/10",
  children,
  slideInterval = 5000,
}) {
  const isHome = variant === "home";

  const [current, setCurrent] = useState(0);

  // =========================================================
  // AUTO SLIDER
  // =========================================================

  useEffect(() => {
    if (!isHome || backgroundImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrent((prev) => {
        return (prev + 1) % backgroundImages.length;
      });
    }, slideInterval);

    return () => {
      clearInterval(interval);
    };
  }, [isHome, backgroundImages, slideInterval]);

  // =========================================================
  // ACTIVE BACKGROUND IMAGE
  // =========================================================

  const activeImage =
    backgroundImages.length > 0
      ? backgroundImages[current]
      : backgroundImage;

  // =========================================================
  // HOME HERO
  // =========================================================

  if (isHome) {
    return (
      <section
        className="
      relative
      mt-[72px]
      w-full
      overflow-visible
      bg-[#F7F5EF]
    "
      >
        {/* =====================================================
        HERO IMAGE AREA
    ===================================================== */}

        <div
          className="
        relative
        h-[430px]
        w-full
        overflow-hidden

        sm:h-[460px]

        md:h-[480px]

        lg:h-[500px]

        xl:h-[580px]
      "
        >
          {/* =================================================
          BACKGROUND SLIDER
      ================================================= */}

          {backgroundImages.length > 0 ? (
            <>
              {backgroundImages.map((image, index) => (
                <div
                  key={index}
                  className="
                absolute
                inset-0
                bg-cover
                bg-center
                bg-no-repeat
                transition-opacity
                duration-1000
                ease-in-out
              "
                  style={{
                    backgroundImage: `url(${image})`,
                    opacity: index === current ? 1 : 0,
                  }}
                />
              ))}
            </>
          ) : (
            activeImage && (
              <div
                className="
              absolute
              inset-0
              bg-cover
              bg-center
              bg-no-repeat
            "
                style={{
                  backgroundImage: `url(${activeImage})`,
                }}
              />
            )
          )}

          {/* =================================================
          HERO OVERLAY
      ================================================= */}

          <div
            className="
          absolute
          inset-0
          bg-gradient-to-r
          from-[#092F31]/65
          via-[#092F31]/25
          to-transparent
        "
          />

          {/* =================================================
          BOTTOM SOFT GRADIENT
      ================================================= */}

          <div
            className="
          pointer-events-none
          absolute
          inset-x-0
          bottom-0
          h-36
          bg-gradient-to-t
          from-[#092F31]/25
          to-transparent
        "
          />

          {/* =================================================
          HERO CONTENT
      ================================================= */}

          <div
            className="
    relative
    z-10
    mx-auto
    flex
    h-full
    w-full
    max-w-[1440px]
    items-center

    px-5

    pt-20
    pb-20

    sm:px-8
    sm:pt-24
    sm:pb-24

    lg:px-12
    lg:pt-28
    lg:pb-28

    xl:px-16
    xl:pt-[145px]
    xl:pb-[145px]
  "
          >
            <div
              className="
                max-w-[600px]
                text-left
              "
            >
              {/* =================================================
                  EYEBROW
              ================================================= */}

              <div
                className="
                  mb-4
                  flex
                  items-center
                  gap-3
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.20em]
                  text-white

                  sm:text-[11px]
                "
              >
                <span
                  className="
                    h-[1px]
                    w-7
                    bg-[#E0B35A]
                  "
                />

                Discover Dubai's finest hotels
              </div>

              {/* =================================================
                  HEADING
              ================================================= */}

              <h1
                className="
                  max-w-[620px]
                  font-serif
                  text-[42px]
                  font-medium
                  leading-[1.02]
                  tracking-[-0.025em]
                  text-white
                  drop-shadow-[0_3px_12px_rgba(0,0,0,0.22)]

                  sm:text-[50px]

                  md:text-[56px]

                  lg:text-[60px]

                  xl:text-[64px]
                "
              >
                {formatHeroTitle(title)}
              </h1>

              {/* =================================================
                  SUBTITLE
              ================================================= */}

              {subtitle && (
                <p
                  className="
                    mt-5
                    max-w-[500px]
                    text-[13px]
                    font-normal
                    leading-6
                    text-white/90

                    sm:text-[14px]
                    sm:leading-6

                    md:text-[15px]
                  "
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* =================================================
              SLIDER DOTS
          ================================================= */}

          {backgroundImages.length > 1 && (
            <div
              className="
                absolute
                bottom-5
                left-1/2
                z-20
                flex
                -translate-x-1/2
                items-center
                gap-2
              "
            >
              {backgroundImages.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => setCurrent(index)}
                  className={`
                    h-1.5
                    rounded-full
                    transition-all
                    duration-300

                    ${index === current
                      ? "w-7 bg-white"
                      : "w-1.5 bg-white/50 hover:bg-white/80"
                    }
                  `}
                />
              ))}
            </div>
          )}
        </div>

        {/* =====================================================
            SEARCH PANEL

            This intentionally overlaps the bottom of the hero
            to match the reference design.
        ===================================================== */}

        {children && (
          <div
            className="
              relative
              z-30
              mx-auto
              -mt-[48px]
              w-[calc(100%-32px)]
              max-w-[1310px]

              sm:-mt-[54px]
              sm:w-[calc(100%-48px)]

              lg:-mt-[45px]
              lg:w-[calc(100%-96px)]

              xl:w-[calc(100%-128px)]
            "
          >
            <div
              className="
                rounded-[18px]
                border
                border-[#E9E4DA]
                bg-white
                p-2.5

                shadow-[0_18px_45px_rgba(15,45,44,0.13)]

                sm:rounded-[20px]
                sm:p-3

                lg:rounded-[18px]
                lg:p-2.5
              "
            >
              {children}
            </div>
          </div>
        )}

        {/* =====================================================
            SPACE BELOW SEARCH
        ===================================================== */}

        <div
          className="
            h-[60px]
            bg-[#F7F5EF]

            sm:h-[70px]

            lg:h-[78px]
          "
        />
      </section>
    );
  }

  // ===========================================================
  // INNER PAGE HERO
  // ===========================================================

  return (
    <section
      className="
    relative
    mt-[72px]
    w-full
    overflow-visible
    bg-[#F7F5EF]
  "
    >
      {/* =====================================================
          BACKGROUND
      ===================================================== */}

      {activeImage && (
        <div
          className="
            absolute
            inset-0
            bg-cover
            bg-center
            bg-no-repeat
          "
          style={{
            backgroundImage: `url(${activeImage})`,
          }}
        />
      )}

      {/* =====================================================
          INNER PAGE OVERLAY
      ===================================================== */}

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-r
          from-[#092F31]/70
          via-[#092F31]/45
          to-[#092F31]/45
        "
      />

      {/* =====================================================
          INNER PAGE CONTENT
      ===================================================== */}

      <div
        className="
          relative
          z-10
          mx-auto
          flex
          min-h-[260px]
          w-full
          max-w-[1440px]
          items-center
          justify-center
          px-5
          py-16
          text-center

          sm:min-h-[300px]
          sm:px-8

          md:min-h-[340px]

          lg:px-12
        "
      >
        <div
          className="
            mx-auto
            max-w-4xl
            text-white
          "
        >
          {/* =================================================
              INNER EYEBROW
          ================================================= */}

          <div
            className="
              mb-4
              inline-flex
              items-center
              gap-3
              text-[10px]
              font-semibold
              uppercase
              tracking-[0.20em]
              text-white
            "
          >
            <span
              className="
                h-[1px]
                w-7
                bg-[#E0B35A]
              "
            />

            Dubai Hotel Rates
          </div>

          {/* =================================================
              INNER TITLE
          ================================================= */}

          <h1
            className="
              font-serif
              text-[38px]
              font-medium
              leading-tight
              tracking-[-0.02em]
              text-white
              drop-shadow-lg

              sm:text-[46px]

              md:text-[54px]

              lg:text-[60px]
            "
          >
            {title}
          </h1>

          {/* =================================================
              INNER SUBTITLE
          ================================================= */}

          {subtitle && (
            <p
              className="
                mx-auto
                mt-4
                max-w-2xl
                text-sm
                leading-6
                text-white/90

                md:text-base
                md:leading-7
              "
            >
              {subtitle}
            </p>
          )}

          {/* =================================================
              ADDITIONAL CONTENT
          ================================================= */}

          {children && (
            <div className="mt-7">
              {children}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


// ===========================================================
// HERO TITLE FORMATTER
// ===========================================================
//
// Makes:
// "Your Perfect Stay in Dubai"
//
// display as:
//
// Your Perfect Stay
// in Dubai
//
// with Dubai in the gold italic style.
//
// It also safely falls back to normal text for other titles.
// ===========================================================

function formatHeroTitle(title) {
  if (!title) {
    return null;
  }

  const normalizedTitle = title.toLowerCase();

  if (normalizedTitle.includes("dubai")) {
    const match = title.match(
      /^(.*?)(\s+in\s+)(dubai)(.*)$/i
    );

    if (match) {
      const before = match[1];
      const after = match[4];

      return (
        <>
          {before}
          <br />

          <span
            className="
              italic
              text-[#E0B35A]
            "
          >
            Dubai
          </span>

          {after}
        </>
      );
    }

    const parts = title.split(/(dubai)/i);

    return parts.map((part, index) => {
      if (part.toLowerCase() === "dubai") {
        return (
          <span
            key={index}
            className="
              italic
              text-[#E0B35A]
            "
          >
            {part}
          </span>
        );
      }

      return (
        <React.Fragment key={index}>
          {part}
        </React.Fragment>
      );
    });
  }

  return title;
}